import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { BASE_URL } from "../utils/helpers";

interface UseAuthenticatedPostOptions<TData, TVariables = unknown>
  extends Omit<UseMutationOptions<TData, unknown, TVariables>, "mutationFn"> {
  tokenKey?: string;
  queryKeyToInvalidate?: readonly unknown[];
}

export function usePostAuth<TData = unknown, TVariables = unknown>(
  endpoint: string,
  options?: UseAuthenticatedPostOptions<TData, TVariables>
): UseMutationResult<TData, unknown, TVariables> {
  const {
    tokenKey = "_token",
    queryKeyToInvalidate,
    onSuccess: providedOnSuccess,
    ...mutationOptions
  } = options || {};
  const queryClient = useQueryClient();

  const mutationFn = async (variables: TVariables): Promise<TData> => {
    const token = localStorage.getItem(tokenKey);

    const url = `${BASE_URL}/${endpoint.replace(/^\//, "")}`;

    // Check if variables is FormData
    const isFormData = variables instanceof FormData;

    // Prepare headers - don't set Content-Type for FormData (browser will set it with boundary)
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: isFormData ? (variables as FormData) : JSON.stringify(variables),
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem(tokenKey);
        window.location.href = "/login";
        throw new Error("Unauthorized - redirecting to login");
      }

      const contentType = response.headers.get("content-type");
      let errorMessage = response.statusText;

      if (contentType && contentType.includes("application/json")) {
        try {
          const errorData = await response.json();

          errorMessage =
            errorData.message ||
            (errorData.errors
              ? Object.values(errorData.errors).flat().join(", ")
              : errorMessage);
        } catch (parseError) {
          const errText = await response.text();
          errorMessage = errText || errorMessage;
        }
      } else {
        const errText = await response.text();
        errorMessage = errText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    return response.json();
  };

  return useMutation({
    mutationFn,
    mutationKey: options?.mutationKey,
    onSuccess: (data: TData, variables: TVariables, context: unknown) => {
      if (queryKeyToInvalidate) {
        queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
      }

      providedOnSuccess?.(data, variables, context, undefined as any);
    },
    ...mutationOptions,
  });
}
